import { useState, useCallback } from 'react'
import { Search, X } from 'lucide-react'
import { useDebounceValue } from 'usehooks-ts'

import { Button } from '@/common/ui/button'
import { Input } from '@/common/ui/input'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import { useQuerySearchMessages } from '../../api/hooks'
import { MessageBubble } from '../MessageBubble'
import { Skeleton } from '@/common/ui/skeleton'

interface SearchMessagesDialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	roomId?: string
	onNavigateToMessage?: (messageId: string, roomId: string) => void
}

export function SearchMessagesDialog({
	open,
	onOpenChange,
	roomId,
	onNavigateToMessage,
}: SearchMessagesDialogProps) {
	const [query, setQuery] = useState('')
	const [debouncedQuery] = useDebounceValue(query, 500)
	const { data, isLoading } = useQuerySearchMessages(
		{ query: debouncedQuery, roomId },
		open && !!debouncedQuery,
	)

	const handleResultClick = useCallback(
		(messageId: string, messageRoomId: string) => {
			if (onNavigateToMessage) {
				onNavigateToMessage(messageId, messageRoomId)
				onOpenChange(false)
			}
		},
		[onNavigateToMessage, onOpenChange],
	)

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-2xl">
				<DialogHeader>
					<DialogTitle>ค้นหาข้อความ</DialogTitle>
					<DialogDescription>
						ค้นหาข้อความในห้องแชทนี้หรือทุกห้อง
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4">
					<div className="flex gap-2">
						<div className="relative flex-1">
							<Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
							<Input
								placeholder="พิมพ์คำค้นหา..."
								value={query}
								onChange={(e) => setQuery(e.target.value)}
								className="pl-9"
							/>
						</div>
						{query && (
							<Button variant="ghost" size="icon" onClick={() => setQuery('')}>
								<X className="h-4 w-4" />
							</Button>
						)}
					</div>

					<div className="max-h-[400px] overflow-y-auto">
						{isLoading ? (
							<div className="space-y-4">
								{Array.from({ length: 3 }).map((_, i) => (
									<Skeleton key={i} className="h-20 w-full" />
								))}
							</div>
						) : debouncedQuery && data ? (
							data.items.length > 0 ? (
								<div className="space-y-4">
									{data.items.map((item) => (
										<button
											type="button"
											key={item.message.id}
											className="w-full cursor-pointer rounded-lg border p-3 text-left transition-colors hover:bg-muted/50"
											onClick={() =>
												handleResultClick(item.message.id, item.room.id)
											}
										>
											<div className="mb-2 flex items-center gap-2 text-sm text-muted-foreground">
												<span className="font-medium">
													{item.room.lineDisplayName || item.room.id}
												</span>
												<span>•</span>
												<span>
													{new Date(item.message.createdAt).toLocaleString(
														'th-TH',
													)}
												</span>
											</div>
											<MessageBubble
												message={item.message}
												showAvatar={false}
											/>
										</button>
									))}
								</div>
							) : (
								<div className="py-8 text-center text-sm text-muted-foreground">
									ไม่พบข้อความที่ค้นหา
								</div>
							)
						) : (
							<div className="py-8 text-center text-sm text-muted-foreground">
								พิมพ์คำค้นหาเพื่อเริ่มค้นหา
							</div>
						)}
					</div>
				</div>
			</DialogContent>
		</Dialog>
	)
}
